"use strict";
/**
 * AlignmentGraph Component
 *
 * Renders the extended React Flow graph showing:
 * Strategy Columns -> Metrics Column -> Use Case Column
 *
 * This visualization helps understand how a strategy connects
 * to specific AI use cases through business metrics.
 *
 * This component owns the dashboard alignment graph rendering
 * and keeps the dashboard feature independent from Admin-only
 * feature folders.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AlignmentGraph = void 0;
var react_1 = __importStar(require("react"));
var StrategyReactFlow_1 = require("../../shared/StrategyReactFlow");
var components_1 = require("../../shared/components");
var constants_1 = require("../../shared/constants");
require("../styles/alignment-graph.scss");
var filterStructureToAffectedChain = function (structure, seedNodeIds) {
    if (seedNodeIds.size === 0) {
        return structure;
    }
    var connectedNodeIds = new Set(seedNodeIds);
    var hasNewNodes = true;
    while (hasNewNodes) {
        hasNewNodes = false;
        structure.forEach(function (column) {
            (column.nodes || []).forEach(function (node) {
                var targetIds = Object.keys(node.edges || {});
                var connectsToAffectedChain = targetIds.some(function (targetId) {
                    return connectedNodeIds.has(targetId);
                });
                if (connectsToAffectedChain && !connectedNodeIds.has(node.id)) {
                    connectedNodeIds.add(node.id);
                    hasNewNodes = true;
                }
            });
        });
    }
    return structure
        .map(function (column) { return (__assign(__assign({}, column), { nodes: (column.nodes || []).filter(function (node) { return connectedNodeIds.has(node.id); }) })); })
        .filter(function (column) { return column.nodes.length > 0; });
};
/**
 * AlignmentGraph - Extended graph visualization
 */
var AlignmentGraphComponent = function (_a) {
    var _b, _c;
    var alignmentData = _a.alignmentData, isLoading = _a.isLoading, useCaseMetricNames = _a.useCaseMetricNames;
    var structure = (_b = alignmentData === null || alignmentData === void 0 ? void 0 : alignmentData.structure) !== null && _b !== void 0 ? _b : [];
    var affectedNodes = (_c = alignmentData === null || alignmentData === void 0 ? void 0 : alignmentData.affectedNodes) !== null && _c !== void 0 ? _c : [];
    var affectedNodeIds = react_1.useMemo(function () { return new Set(affectedNodes.map(function (node) { return node.nodeId; })); }, [affectedNodes]);
    var affectedNodeScores = react_1.useMemo(function () {
        return affectedNodes.reduce(function (scores, node) {
            scores[node.nodeId] = node.score;
            return scores;
        }, {});
    }, [affectedNodes]);
    var normalizedMetricNameSet = react_1.useMemo(function () {
        return new Set((useCaseMetricNames || [])
            .map(function (metricName) { return metricName.trim().toLowerCase(); })
            .filter(function (metricName) { return metricName.length > 0; }));
    }, [useCaseMetricNames]);
    var metricMatchedNodeIds = react_1.useMemo(function () {
        if (normalizedMetricNameSet.size === 0) {
            return new Set();
        }
        var matched = new Set();
        structure.forEach(function (column) {
            (column.nodes || []).forEach(function (node) {
                var normalizedNodeName = node.name.trim().toLowerCase();
                if (normalizedMetricNameSet.has(normalizedNodeName)) {
                    matched.add(node.id);
                }
            });
        });
        return matched;
    }, [normalizedMetricNameSet, structure]);
    var chainSeedNodeIds = metricMatchedNodeIds.size > 0 ? metricMatchedNodeIds : affectedNodeIds;
    var filteredStructure = react_1.useMemo(function () { return filterStructureToAffectedChain(structure, chainSeedNodeIds); }, [chainSeedNodeIds, structure]);
    react_1.useEffect(function () {
        console.log('[WsUseCaseAlignment] Filtered structure for use-case metric chain', {
            originalColumnCount: structure.length,
            filteredColumnCount: filteredStructure.length,
            affectedNodeCount: affectedNodeIds.size,
            useCaseMetricNameCount: normalizedMetricNameSet.size,
            useCaseMetricNames: Array.from(normalizedMetricNameSet),
            metricMatchedNodeIds: Array.from(metricMatchedNodeIds),
            affectedNodeIds: Array.from(affectedNodeIds),
            chainSeedNodeIds: Array.from(chainSeedNodeIds),
            filteredNodeIds: filteredStructure.flatMap(function (column) { return column.nodes.map(function (node) { return node.id; }); }),
        });
    }, [
        affectedNodeIds,
        chainSeedNodeIds,
        filteredStructure,
        metricMatchedNodeIds,
        normalizedMetricNameSet,
        structure.length,
    ]);
    if (isLoading || !alignmentData) {
        return react_1.default.createElement(components_1.SkeletonGraph, { height: constants_1.DEFAULT_GRAPH_HEIGHT });
    }
    return (react_1.default.createElement(StrategyReactFlow_1.StrategyReactFlow, { columns: filteredStructure, isLoading: false, affectedNodeIds: affectedNodeIds, affectedNodeScores: affectedNodeScores }));
};
var AlignmentGraph = react_1.default.memo(AlignmentGraphComponent);
exports.AlignmentGraph = AlignmentGraph;
AlignmentGraph.displayName = 'AlignmentGraph';
